import DatePicker from "src/components/date-picker";
import { UserDetails, PersonalInfoFormValidations, UserFormRequest } from "./models";
import { DialogContent, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import CustomInput from "src/components/forms/custom-input";
import CustomSelect from "src/components/forms/custom-select";
import useApi from "src/configs/api";
import { useEffect, useState } from "react";
import {useAlert} from "src/hooks/use-alert";
import { FormOptionDto, MasterDto } from "src/models";
import { fromDateOnly, toDateOnly, toOptions } from "src/helpers/global";
import { set } from "date-fns";

export interface PersonalInfoDialogProps {
    data?: UserDetails.PersonalInfo | null | undefined;
    onSubmit? : () => void;
    onClose? : () => void;
}

const toPersonalInfoDto = (personalInfo? : UserDetails.PersonalInfo | null) : UserFormRequest.PersonalInfo => {
    return {
        firstName: personalInfo?.firstName? personalInfo.firstName : "",
        lastName: personalInfo?.lastName ? personalInfo.lastName : "",
        dob: personalInfo?.dob? personalInfo.dob.toString() : undefined,
        genderEnumId: personalInfo?.gender?.value ? Number(personalInfo.gender.value) : null
    };
};

export const PersonalInfoDialog = ({ data, onSubmit, onClose } : PersonalInfoDialogProps) => {
    const api = useApi();
    const { showSuccess, showError} = useAlert();
    const [genders, setGenders] = useState<FormOptionDto[]>([]);
    const [updatedData, setUpdatedData] = useState<UserFormRequest.PersonalInfo>( () => toPersonalInfoDto(data));
    const [formValidations, setFormValidations] = useState<Partial<PersonalInfoFormValidations>>({});
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        fetchFormReferences();
    }, []);

     // Ensure data resets when modal opens with new data
    useEffect(() => {
        if (data) setUpdatedData(toPersonalInfoDto(data));
    }, [data]);

    const fetchFormReferences = async () => {
        try
        {
            const res = await api.get("/master-data/gender");
            if (res.status === 200) 
            {               
                const categoryOptions = toOptions(res.data.data, "idEnum", "value");
                setGenders(categoryOptions);
            }
            else
                return null;
        }
        catch{
            showError("An error occured while trying to process your request. Please try again later.");
            onClose?.();
        }

    }

    const handleDateChange = (date : Date | null) => 
    {
        setUpdatedData((prev) => ({
            ...prev,
            dob : date ? toDateOnly((date)) : undefined
        }))

        setFormValidations((prev) => ({
            ...prev,
            dob : undefined
        }))
    }

    const handleChanges = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {

        const {name, value} = e.target;
        
        setUpdatedData((prev) => ({
            ...prev,
            [name] : value
        }))       
        setFormValidations((prev) => ({
            ...prev,
            [name] : undefined
        }))
    }

    const handleSubmit  = async () => {
        setIsProcessing(true);

        if(!handleValidations()) {return;}

        try {

            console.log("Updated Data: ", updatedData);
            const response = await updatePersonalInfo(updatedData);
             if (!response.success) 
                showError(response.message);
            
            showSuccess("Personal information updated successfully.");
            await onSubmit?.();
            onClose?.();
        } 
        catch (error) {
            showError("An error occured while trying to process your request. Please try again later.");
        } finally {
            setIsProcessing(false);
        }
    }

    const updatePersonalInfo = async (data: Partial<UserFormRequest.PersonalInfo>) => {
            const response = await api.put("/user/me/personal-info", data);
            return response.data;
        };

    const handleValidations  = async () => {
        var isFormValid : boolean = true;
        let validations : Partial<PersonalInfoFormValidations> = {};

        if(!updatedData.firstName || !updatedData.lastName){
            isFormValid = false;
            validations.firstName = !updatedData.firstName ? "First name is required" : undefined;
            validations.lastName = !updatedData.lastName ? "Last name is required" : undefined;
        }

        if (updatedData.dob) 
        {
            let dob = new Date(updatedData.dob);            
            let today = new Date();
            if (dob > today) {
                isFormValid = false;
                validations.dob = "Date of birth cannot be in the future";
            }
        }

        setFormValidations(validations);

        return isFormValid;

    }
    
    if(!data) return null;
    return (

        <DialogContent showCloseButton={false} className="sm:max-w-md text-base">
            <DialogHeader >
                <DialogTitle className="text-large text-center">Update Personal Information</DialogTitle>
            </DialogHeader>

            <form className="space-y-3" action={handleSubmit} method="POST">
                <div className="grid md:grid-cols-2 gap-2">
                <div>
                    <CustomInput
                        format="text"
                        name="firstName"
                        label="First Name"
                        value={updatedData.firstName}
                        onChange={(val) => handleChanges({ target: { name: "firstName", value: val } } as React.ChangeEvent<HTMLInputElement>)}
                        required
                        errorMessage={formValidations.firstName}
                    />
                </div>
                <div>
                    <CustomInput
                        format="text"
                        name="lastName"
                        label="Last Name"
                        value={updatedData.lastName}
                        onChange={(val) => handleChanges({ target: { name: "lastName", value: val } } as React.ChangeEvent<HTMLInputElement>)}
                        required
                        errorMessage={formValidations.lastName}
                    />                    
                </div>
                </div>
                <div>
                    <label htmlFor="dob" className="block text-sm font-semibold leading-6 text-gray-900">
                        Date of Birth 
                    </label>
                    <div className="mt-2">
                        <DatePicker name="dob"  
                        value={updatedData.dob ? new Date(updatedData.dob) : null}
                        onChange={handleDateChange}
                        showClearButton
                        errorMessage={formValidations.dob}
                        />
                    </div>
                </div>
                <div>
                    <CustomSelect 
                        options={genders} 
                        label="Gender"
                        name="gender"
                        value={updatedData.genderEnumId ?? ""}
                        placeholder="Select gender (optional)"
                        onChange={(val) => handleChanges({ target: { name: "genderEnumId", value: val } } as React.ChangeEvent<HTMLInputElement>)}

                         />
                </div>
                <div className="grid md:grid-cols-3 gap-2 my-5">
                    <div className="md:col-span-2 md:order-last">
                        <button 
                            type='submit'
                            className="block w-full rounded-md border-1 py-2.5 px-2.5 
                            bg-black text-white font-bold text-sm
                            text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
                        >
                            Save Changes
                        </button>
                    </div>
                    <div>
                        <button 
                            type='button'
                            className="block w-full rounded-md border-1 py-2.5 px-2.5 
                            font-bold text-sm
                            text-red-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-500 hover:text-white  focus:ring-2 focus:ring-inset focus:ring-red-600 sm:leading-6"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
           
        </DialogContent>
    )
}